import router from '@/router';
import api from '../api';
import constants from '../../config/constants';

const { URL } = constants;

const SESSION_STORAGE_KEYS = [
  'orbital-access-token',
  'totp-challenge-token',
  'new-account',
];

function clearStoredSession() {
  SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

const initialState = () => ({
  idTokenPayload: null,
  userId: null,
  isAuthenticated: false,
  organizationIsInactive: false,
  currentUser: {},
  jwtToken: null,
  sessionInitialized: false,
  sessionValidationInProgress: false,
  sessionExpirationInProgress: false,
});

const state = initialState();

const getters = {
  idTokenPayload: ({ idTokenPayload }) => idTokenPayload,
  isAuthenticated: ({ isAuthenticated }) => isAuthenticated,
  organizationIsInactive: ({ organizationIsInactive }) => organizationIsInactive,
  currentUser: ({ currentUser }) => currentUser,
  sessionInitialized: ({ sessionInitialized }) => sessionInitialized,
  sessionValidationInProgress: ({ sessionValidationInProgress }) => sessionValidationInProgress,
  isSuperUser: ({ currentUser }) => (currentUser && currentUser.isSuperuser ? currentUser.isSuperuser : false), // eslint-disable-line
  canScanAnything: ({ currentUser }) => (currentUser && currentUser.canScanAnything
    ? currentUser.canScanAnything : false),
};

const mutations = {
  CLEAR_STATE: (state, options = {}) => {
    state.authenticationError = '';
    state.idTokenPayload = null;
    state.userId = null;
    state.isAuthenticated = false;
    state.organizationIsInactive = false;
    state.currentUser = {};
    state.jwtToken = null;
    state.sessionInitialized = options.sessionInitialized !== false;
    state.sessionValidationInProgress = false;
    state.sessionExpirationInProgress = Boolean(options.sessionExpirationInProgress);
  },
  CLEAR_AUTHENTICATION_ERROR: (state) => {
    state.authenticationError = '';
  },
  SET_TOKEN_PAYLOAD: (state, payload) => {
    state.idTokenPayload = payload;
  },
  SET_ISAUTHENTICATED: (state, payload) => {
    state.isAuthenticated = payload;
  },
  SET_ORGANIZATION_INACTIVE: (state, payload) => {
    state.organizationIsInactive = payload;
  },
  UPDATE_CURRENT_USER: (state, payload) => {
    state.currentUser = payload;
  },
  SET_SESSION_INITIALIZED: (state, payload) => {
    state.sessionInitialized = payload;
  },
  SET_SESSION_VALIDATION_IN_PROGRESS: (state, payload) => {
    state.sessionValidationInProgress = payload;
  },
  SET_SESSION_EXPIRATION_IN_PROGRESS: (state, payload) => {
    state.sessionExpirationInProgress = payload;
  },
};

function resetApplicationState(commit) {
  commit('RESET_REPORT_STATE', null, { root: true });
  commit('CLEAR_ERRORS', true, { root: true });
  commit('CLEAR_FIELD_ERRORS', true, { root: true });
  commit('RESET_SUBSCRIPTION_STATE', null, { root: true });
  commit('RESET_UI_STATE', null, { root: true });
}

const actions = {
  fetchCurrentUser: ({ commit }) => api.get('users/0/').then(({ data }) => {
    commit('UPDATE_CURRENT_USER', data);
  }),
  login: async ({ commit, state }, { email, password }) => {
    const { sessionExpirationInProgress } = state;
    clearStoredSession();
    commit('CLEAR_STATE', {
      sessionInitialized: true,
      sessionExpirationInProgress,
    });
    resetApplicationState(commit);
    commit('UPDATE_IS_LOADING', true, { root: true });
    try {
      const { data } = await api.post('login/', { username: email, password });
      localStorage.setItem('totp-challenge-token', data.access);
      const { data: res } = await api.get('is-enrolled/', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('totp-challenge-token')}`,
        },
      });
      if (res.enrolled) {
        await router.push('totp-verify');
      } else {
        await router.push('totp-enroll');
      }
    } catch (error) {
      const description = error.description
        || (error.response && error.response.data && error.response.data.detail)
        || 'Unable to log in with those credentials.';
      commit('UPDATE_GLOBAL_ERRORS', [description]);
    } finally {
      commit('UPDATE_IS_LOADING', false, { root: true });
    }
  },
  totpEnroll: async () => api.get('setup-totp/', {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('totp-challenge-token')}`,
    },
  }),
  totpRecoveryTokens: async () => api.get('otp-recovery-device/', {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('totp-challenge-token')}`,
    },
  }),
  verifyTotp: async ({ commit, dispatch }, code) => {
    const { data } = await api.post('verify-totp/', code, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('totp-challenge-token')}`,
      },
    });
    localStorage.setItem('orbital-access-token', data.token);
    localStorage.removeItem('totp-challenge-token');
    commit('SET_SESSION_EXPIRATION_IN_PROGRESS', false);
    return dispatch('checkAuthentication');
  },
  logout: ({ commit }) => {
    clearStoredSession();
    commit('CLEAR_STATE');
    resetApplicationState(commit);

    return new Promise((resolve) => resolve());
  },

  expireSession: async ({ commit, state }) => {
    if (state.sessionExpirationInProgress) return false;

    commit('SET_SESSION_EXPIRATION_IN_PROGRESS', true);
    clearStoredSession();
    commit('CLEAR_STATE', {
      sessionInitialized: true,
      sessionExpirationInProgress: true,
    });
    resetApplicationState(commit);

    if (router.currentRoute.name !== 'login') {
      await router.replace({
        name: 'login',
        query: { reason: 'session-expired' },
      }).catch(() => {});
    }
    return true;
  },

  checkAuthentication: async ({ commit, state }) => {
    if (state.sessionValidationInProgress) return 'pending';

    const accessToken = localStorage.getItem('orbital-access-token');
    if (!accessToken) {
      if (!state.sessionExpirationInProgress) {
        commit('CLEAR_STATE');
        resetApplicationState(commit);
      }
      return 'anonymous';
    }

    commit('SET_SESSION_VALIDATION_IN_PROGRESS', true);
    try {
      const { data } = await api.get('users/0/', {
        incrementRequestCount: false,
        suppressGlobalErrors: true,
      });
      commit('UPDATE_CURRENT_USER', data);
      commit('SET_ORGANIZATION_INACTIVE', false);
      commit('SET_SESSION_EXPIRATION_IN_PROGRESS', false);
      commit('SET_ISAUTHENTICATED', true);
      return 'authenticated';
    } catch (error) {
      const status = error && error.response && error.response.status;
      if (status === 403) {
        commit('UPDATE_CURRENT_USER', {});
        commit('SET_ORGANIZATION_INACTIVE', true);
        commit('SET_ISAUTHENTICATED', false);
        return 'inactive';
      }
      if (status === 401 || (error && error.isSessionExpired)) return 'expired';

      // A transient API failure is not evidence that the token or organization is invalid.
      commit('SET_ISAUTHENTICATED', true);
      return 'unavailable';
    } finally {
      commit('SET_SESSION_VALIDATION_IN_PROGRESS', false);
      commit('SET_SESSION_INITIALIZED', true);
    }
  },

  register: async ({ commit, dispatch }, registrationRequest) => {
    commit('UPDATE_IS_LOADING', true, { root: true });
    try {
      const { data } = await api.post('registration/', registrationRequest);
      if (data && data.statusCode && data.statusCode !== 200) {
        if (data.message && data.message.includes('PasswordStrengthError')) {
          commit('UPDATE_GLOBAL_ERRORS', ['Your password is too weak.']);
        } else {
          commit('UPDATE_GLOBAL_ERRORS', [
            'Your registration code is no longer valid, please reach out to Orbital support.',
          ]);
        }
        return;
      }
      dispatch('login', {
        email: registrationRequest.emailAddress,
        password: registrationRequest.password,
      });
    } catch (err) {
      if (
        err.response.data
        && err.response.data.nonFieldErrors
        && err.response.data.nonFieldErrors.includes('password and password_confirmation must match')
      ) {
        commit('UPDATE_GLOBAL_ERRORS', ['Your passwords must match!']);
      } else {
        commit('UPDATE_GLOBAL_ERRORS', [
          'Your registration code is no longer valid, please reach out to Orbital support.',
        ]);
      }
    } finally {
      commit('UPDATE_IS_LOADING', false, { root: true });
    }
  },

  requestForgotPassword: async ({ commit }, email) => {
    commit('UPDATE_IS_LOADING', true, { root: true });
    try {
      await api.post('request-password-change/', { email, url: URL });
    } catch (error) {
      commit('UPDATE_GLOBAL_ERRORS', [error]);
    } finally {
      commit('UPDATE_IS_LOADING', false, { root: true });
    }
  },

  updatePassword: async ({ commit }, password) => {
    commit('UPDATE_IS_LOADING', true, { root: true });
    try {
      await api.post('update-password/', { password }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('totp-challenge-token')}`,
        },
      });
    } catch (error) {
      commit('UPDATE_GLOBAL_ERRORS', [error]);
    } finally {
      commit('UPDATE_IS_LOADING', false, { root: true });
    }
  },

  validateOrganization: ({ dispatch }) => dispatch('checkAuthentication'),

  signup: ({ commit, dispatch }, signupRequest) => {
    commit('UPDATE_IS_LOADING', true, { root: true });
    return api
      .post('organizations/', signupRequest)
      .then(() => dispatch('login', {
        email: signupRequest.adminEmail,
        password: signupRequest.adminPassword,
      }))
      .catch((err) => {
        if (err.response && err.response.data) commit('UPDATE_FIELD_ERRORS', err.response.data);
        commit('UPDATE_GLOBAL_ERRORS', [
          'There was an error creating your organization. Please check all fields and try again.',
        ]);
      })
      .finally(() => {
        commit('UPDATE_IS_LOADING', false, { root: true });
      });
  },

  requestSignupQuote: async ({ commit }, quoteRequest) => {
    try {
      const { data } = await api.post('organization-quotes/', quoteRequest, {
        suppressGlobalErrors: true,
      });
      return data;
    } catch (error) {
      if (error.response && error.response.data) {
        commit('UPDATE_FIELD_ERRORS', error.response.data);
      }
      throw error;
    }
  },

  fetchSignupQuote: async (context, quoteId) => {
    const { data } = await api.get(`organization-quotes/${quoteId}/`, {
      incrementRequestCount: false,
      suppressGlobalErrors: true,
    });
    return data;
  },

  createOrganization: async ({ commit }, signupRequest) => {
    commit('UPDATE_IS_LOADING', true, { root: true });
    try {
      const { data } = await api.post('organizations/', signupRequest);
      return data;
    } catch (error) {
      if (error.response && error.response.data) {
        commit('UPDATE_FIELD_ERRORS', error.response.data);
      }
      commit('UPDATE_GLOBAL_ERRORS', [
        'There was an error creating your organization. Please check all fields and try again.',
      ]);
      throw error;
    } finally {
      commit('UPDATE_IS_LOADING', false, { root: true });
    }
  },
};

export default {
  actions,
  state,
  getters,
  mutations,
};
